/**
 * Zakat calculator — Hanafi-default, multi-madhhab aware.
 * Religious accuracy: stick to mainstream consensus. For estimation only.
 *
 * Rates:
 *   Lunar year (354 days): 2.5%
 *   Gregorian (365.25 days): 2.5775%  (≈ 2.58%)
 *
 * Nisab (per Hanafi standard): silver 612.36 g, gold 87.48 g.
 * Some scholars use 595 g / 85 g — exposed as constants.
 */

import { GRAMS_PER_VORI } from "@/lib/units/vori";
import { getMadhhab, type MadhhabId } from "@/data/madhhabs";

export const NISAB_SILVER_G = 612.36; // 52.5 vori
export const NISAB_GOLD_G = 87.48; // 7.5 vori

export const ZAKAT_RATE_LUNAR = 0.025;
export const ZAKAT_RATE_GREGORIAN = 0.025775;

export type YearType = "lunar" | "gregorian";
export type NisabBasis = "silver" | "gold";

export interface GoldHoldings {
  k22Vori: number;
  k21Vori: number;
  k18Vori: number;
  otherVori: number; // generic / traditional
}

export interface AssetInputs {
  // gold per carat (vori)
  gold: GoldHoldings;
  silverVori: number;

  // cash & bank (BDT)
  cashInHand: number;
  savings: number;
  current: number;
  fdr: number;
  dps: number;
  sanchaypatra: number;

  // digital wallets
  bkash: number;
  nagad: number;
  rocket: number;
  otherWallet: number;

  // investments
  stocks: number;
  bonds: number;
  mutualFunds: number;
  insuranceBonus: number;
  crypto: number;

  // business
  inventory: number;
  receivables: number;
  rawMaterials: number;

  // property (commercial only)
  resaleProperty: number;
  resaleLand: number;
}

export interface LiabilityInputs {
  personalLoans: number;
  creditCard: number;
  billsDue: number;
  businessDebts: number;
  mortgageInstallment: number;
}

export interface PriceInputs {
  /** BDT per vori */
  gold22k: number;
  gold21k: number;
  gold18k: number;
  goldOther: number;
  silver: number;
}

export interface ZakatInputs {
  madhhab: MadhhabId;
  yearType: YearType;
  nisabBasis: NisabBasis;
  prices: PriceInputs;
  assets: AssetInputs;
  liabilities: LiabilityInputs;
}

export interface ZakatBreakdown {
  goldValue: number;
  silverValue: number;
  cashAndBank: number;
  wallets: number;
  investments: number;
  business: number;
  property: number;
}

export interface ZakatResult {
  breakdown: ZakatBreakdown;
  totalAssets: number;
  totalLiabilities: number;
  netWealth: number;
  nisabBdt: number;
  nisabBasis: NisabBasis;
  rate: number;
  obligatory: boolean;
  zakatDue: number;
  // helpful display values
  silverNisabBdt: number;
  goldNisabBdt: number;
}

export const EMPTY_GOLD: GoldHoldings = { k22Vori: 0, k21Vori: 0, k18Vori: 0, otherVori: 0 };

export const EMPTY_ASSETS: AssetInputs = {
  gold: { ...EMPTY_GOLD },
  silverVori: 0,
  cashInHand: 0,
  savings: 0,
  current: 0,
  fdr: 0,
  dps: 0,
  sanchaypatra: 0,
  bkash: 0,
  nagad: 0,
  rocket: 0,
  otherWallet: 0,
  stocks: 0,
  bonds: 0,
  mutualFunds: 0,
  insuranceBonus: 0,
  crypto: 0,
  inventory: 0,
  receivables: 0,
  rawMaterials: 0,
  resaleProperty: 0,
  resaleLand: 0,
};

export const EMPTY_LIABILITIES: LiabilityInputs = {
  personalLoans: 0,
  creditCard: 0,
  billsDue: 0,
  businessDebts: 0,
  mortgageInstallment: 0,
};

function n(x: number | undefined): number {
  const v = Number(x);
  return Number.isFinite(v) && v > 0 ? v : 0;
}

export function goldValueBdt(gold: GoldHoldings, prices: PriceInputs): number {
  return (
    n(gold.k22Vori) * n(prices.gold22k) +
    n(gold.k21Vori) * n(prices.gold21k) +
    n(gold.k18Vori) * n(prices.gold18k) +
    n(gold.otherVori) * n(prices.goldOther)
  );
}

export function silverValueBdt(silverVori: number, prices: PriceInputs): number {
  return n(silverVori) * n(prices.silver);
}

export function nisabFromSilverPrice(silverPerVori: number, grams = NISAB_SILVER_G): number {
  const perGram = silverPerVori / GRAMS_PER_VORI;
  return perGram * grams;
}

export function nisabFromGoldPrice(gold22kPerVori: number, grams = NISAB_GOLD_G): number {
  const perGram = gold22kPerVori / GRAMS_PER_VORI;
  return perGram * grams;
}

export function calculateZakat(input: ZakatInputs): ZakatResult {
  const { assets: a, liabilities: l, prices, madhhab, yearType, nisabBasis } = input;
  const m = getMadhhab(madhhab);

  const breakdown: ZakatBreakdown = {
    goldValue: goldValueBdt(a.gold, prices),
    silverValue: silverValueBdt(a.silverVori, prices),
    cashAndBank:
      n(a.cashInHand) + n(a.savings) + n(a.current) + n(a.fdr) + n(a.dps) + n(a.sanchaypatra),
    wallets: n(a.bkash) + n(a.nagad) + n(a.rocket) + n(a.otherWallet),
    investments:
      n(a.stocks) + n(a.bonds) + n(a.mutualFunds) + n(a.insuranceBonus) + n(a.crypto),
    business: n(a.inventory) + n(a.receivables) + n(a.rawMaterials),
    property: n(a.resaleProperty) + n(a.resaleLand),
  };

  const totalAssets =
    breakdown.goldValue +
    breakdown.silverValue +
    breakdown.cashAndBank +
    breakdown.wallets +
    breakdown.investments +
    breakdown.business +
    breakdown.property;

  const totalLiabilities =
    n(l.personalLoans) +
    n(l.creditCard) +
    n(l.billsDue) +
    n(l.businessDebts) +
    n(l.mortgageInstallment);

  const netWealth = Math.max(0, totalAssets - totalLiabilities);

  const silverNisabBdt = nisabFromSilverPrice(prices.silver);
  const goldNisabBdt = nisabFromGoldPrice(prices.gold22k);

  // Choose Nisab — UI lets user pick, but combineAssetsForNisab biases the default.
  const effectiveBasis: NisabBasis = nisabBasis;
  const nisabBdt = effectiveBasis === "silver" ? silverNisabBdt : goldNisabBdt;

  const rate = yearType === "lunar" ? ZAKAT_RATE_LUNAR : ZAKAT_RATE_GREGORIAN;
  const obligatory = netWealth >= nisabBdt && nisabBdt > 0;
  const zakatDue = obligatory ? netWealth * rate : 0;

  // madhhab.combineAssetsForNisab is informational here — UI uses notes
  void m;

  return {
    breakdown,
    totalAssets,
    totalLiabilities,
    netWealth,
    nisabBdt,
    nisabBasis: effectiveBasis,
    rate,
    obligatory,
    zakatDue,
    silverNisabBdt,
    goldNisabBdt,
  };
}
